import { useEffect, useState } from 'react';
import type { ICustomControlDiagnosticReportReadProps } from '../../interfaces/customControlDiagnosticReportReadProps';
import type { IDermaCV1DiagnosticReportAddEditCustomControlData } from './DermaCV1DiagnosticReportAddEdit';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { DermatologyPolicies } from '../../../../../../common/validation/customControls/dermatology';
import { translateKey } from '../../../../../translate';
import { DiagnosticReportReadButtons } from '../../../../elements/carePathButtons/DiagnosticReportReadButtons';
import { FormDivider, FormParagraph, HeaderDivider } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { List, ListItem } from '../../../../elements/List/List';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { Icd10SelectionsRead } from '../../utils/v1/Icd10/Icd10SelectionsRead';
import { DermaCV1DiagnosticReportSummary } from './DermaCV1DiagnosticReportSummary';

export function DermaCV1DiagnosticReportRead(props: ICustomControlDiagnosticReportReadProps) {
    const [isFooterVisible, setIsFooterVisible] = useState(false);
    const customControlData: IDermaCV1DiagnosticReportAddEditCustomControlData =
        props.diagnosticReport.customControlData;

    if (!customControlData) {
        return null;
    }

    const beleid =
        customControlData.beleid !== DermatologyPolicies.anders
            ? customControlData.beleid
            : customControlData.beleidAndersText;

    useEffect(() => {
        checkIsFooterVisible();
    }, [props.diagnosticReport]);

    return (
        <ProcessContainer>
            <ProcessItemHeader
                date={props.diagnosticReport && props.diagnosticReport.dateSent}
                isFirstItem={props.isFirstItem}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                organizationName={props.diagnosticReport.performerOrganization?.name}
                practitionerName={
                    props.diagnosticReport.performerPractitioner
                        ? getDisplayName(props.diagnosticReport.performerPractitioner)
                        : undefined
                }
                process={props.process}
                title={translateKey('diagnosticReport.category_consultReport')}
            />

            {!props.itemState.isOpen && <DermaCV1DiagnosticReportSummary {...props} />}

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                {customControlData.bevindingen && (
                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('diagnosticReport.findings')}</FormLabel>
                        <InputContainer>
                            <FormParagraph bold>{customControlData.bevindingen}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {customControlData.kwaliteit_fotos_voldoende && (
                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('diagnosticReport.qualityPhotosSufficient')}</FormLabel>
                        <InputContainer>
                            <FormParagraph>
                                {translateKey(`common.${customControlData.kwaliteit_fotos_voldoende}`)}
                            </FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {props.diagnosticReport.diagnosis && (
                    <Icd10SelectionsRead
                        diagnosis={props.diagnosticReport.diagnosis}
                        hideTitle
                        showWorkingDiagnosis
                    />
                )}
                <FormDivider />
                <HeaderDivider text={translateKey('diagnosticReport.advice')} />

                {(customControlData.omschrijving_beleid || beleid) && (
                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('diagnosticReport.policy')}</FormLabel>
                        <InputContainer>
                            <List>
                                {customControlData.omschrijving_beleid && (
                                    <ListItem bold>{customControlData.omschrijving_beleid}</ListItem>
                                )}

                                {beleid && <ListItem bold>{beleid}</ListItem>}
                            </List>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {customControlData.bezoek_aan_specialist_nu_nodig && (
                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('diagnosticReport.visitToSpecialistNecessaryNow')}</FormLabel>
                        <InputContainer>
                            <FormParagraph bold>{customControlData.bezoek_aan_specialist_nu_nodig}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {customControlData.urgentie && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Urgentie</FormLabel>
                        <InputContainer>
                            <FormParagraph bold>{customControlData.urgentie}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {customControlData.toelichting && props.itemState.isOpen && (
                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('diagnosticReport.remarks')}</FormLabel>
                        <InputContainer>
                            <FormParagraph bold>{customControlData.toelichting}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}
            </CustomControlCollapseBody>

            {isFooterVisible && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('diagnosticReport.hideConsultReport')}
                    labelMore={translateKey('diagnosticReport.showConsultReport')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}
            <DiagnosticReportReadButtons {...props} />
        </ProcessContainer>
    );

    function checkIsFooterVisible() {
        if (customControlData.bevindingen || props.diagnosticReport.diagnosis || customControlData.toelichting) {
            setIsFooterVisible(true);
        }
    }
}
